import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, message as antdMessage } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { api } from '../utils/api';
import BulleMessage from '../components/assistant/BulleMessage';
import IndicateurSaisie from '../components/assistant/IndicateurSaisie';
import PanneauConfirmation from '../components/assistant/PanneauConfirmation';
import RecapitulatifCommande from '../components/assistant/RecapitulatifCommande';

/** Suggestions affichées tant que la conversation est vide. */
const EXEMPLES_DE_DEMANDES = [
  'Quelles commandes ont été annulées aujourd’hui ?',
  'Liste les commandes avec la note non décroché aujourd’hui.',
  'Prépare une relance WhatsApp pour les clients non décrochés.',
  'SKU ABC-123 : peux-tu vérifier ce produit avant de créer une commande ?'
];

/**
 * Affiche un assistant commercial limité aux données de commandes du compte.
 * L'écran conserve seulement l'historique de la session navigateur et propose
 * une confirmation explicite avant toute campagne WhatsApp groupée.
 * Les éléments de présentation vivent dans `components/assistant/`.
 */
const CommerceAssistant = () => {
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState([]);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  /** Ancre invisible en fin de fil pour ramener la vue sur le dernier message. */
  const finDuFilRef = useRef(null);
  /** Référence de la zone de saisie pour lui rendre le focus après une suggestion. */
  const zoneSaisieRef = useRef(null);

  /** Charge le solde disponible dès l'ouverture afin de rendre le coût visible. */
  useEffect(() => {
    const loadWallet = async () => {
      try {
        const response = await api.get('/commerce-assistant/wallet');
        setCreditBalance(response.data.balance);
      } catch (error) {
        console.error('Impossible de charger le solde IA:', error);
      }
    };
    loadWallet();
  }, []);

  /** Fait défiler le fil jusqu'au dernier message ou à l'indicateur d'attente. */
  useEffect(() => {
    finDuFilRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading]);

  /**
   * Envoie la demande à l'API, transmet un contexte de conversation réduit et
   * ajoute la réponse de l'assistant sans enregistrer de données localement.
   */
  const sendQuestion = async () => {
    const question = draft.trim();
    if (!question || loading) return;

    const nextMessages = [...messages, { role: 'user', content: question }];
    setMessages(nextMessages);
    setDraft('');
    setLoading(true);

    try {
      const response = await api.post('/commerce-assistant/chat', {
        message: question,
        history: messages.slice(-10)
      });
      setMessages((currentMessages) => [...currentMessages, {
        role: 'assistant',
        content: response.data.message,
        productPreview: response.data.productPreview || null
      }]);
      setPendingConfirmation(response.data.confirmation || null);
      if (Number.isInteger(response.data.balance)) setCreditBalance(response.data.balance);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Impossible de joindre l’assistant.';
      setMessages((currentMessages) => [...currentMessages, { role: 'assistant', content: `Erreur : ${errorMessage}` }]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Confirme le brouillon affiché et délègue l'envoi à la file WhatsApp déjà
   * en place. Le résultat indique des messages mis en file, pas une lecture client.
   */
  const confirmCampaign = async () => {
    if (!pendingConfirmation || sendingCampaign) return;

    setSendingCampaign(true);
    try {
      const response = await api.post('/commerce-assistant/confirm-campaign', { token: pendingConfirmation.token });
      const { queued, failed, total } = response.data;
      setMessages((currentMessages) => [...currentMessages, {
        role: 'assistant',
        content: `Campagne confirmée : ${queued} message(s) sur ${total} ont été ajoutés à la file WhatsApp${failed ? `, ${failed} n’ont pas pu être ajoutés.` : '.'}`
      }]);
      antdMessage.success('La campagne a été ajoutée à la file WhatsApp.');
      setPendingConfirmation(null);
    } catch (error) {
      antdMessage.error(error.response?.data?.error || 'La confirmation a échoué.');
    } finally {
      setSendingCampaign(false);
    }
  };

  /**
   * Confirme le brouillon de commande seulement après lecture du récapitulatif.
   * Le serveur relit le stock et les prix au moment du clic, puis délègue la
   * création au même service métier que le formulaire manuel de commandes.
   */
  const confirmOrder = async () => {
    if (!pendingConfirmation || pendingConfirmation.type !== 'order' || sendingCampaign) return;

    setSendingCampaign(true);
    try {
      const response = await api.post('/commerce-assistant/confirm-order', { token: pendingConfirmation.token });
      const orderNumber = response.data.order?.order_number || response.data.order?.id;
      setMessages((currentMessages) => [...currentMessages, {
        role: 'assistant',
        content: `Commande ${orderNumber || ''} créée avec succès.`.trim()
      }]);
      antdMessage.success(`Commande ${orderNumber || ''} créée avec succès.`.trim());
      setPendingConfirmation(null);
    } catch (error) {
      antdMessage.error(error.response?.data?.error || 'La création de la commande a échoué.');
    } finally {
      setSendingCampaign(false);
    }
  };

  /**
   * Permet d'envoyer le message avec Entrée tout en conservant Maj+Entrée pour
   * écrire une question sur plusieurs lignes.
   */
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendQuestion();
    }
  };

  /** Pré-remplit la zone de saisie avec une suggestion et lui rend le focus. */
  const utiliserSuggestion = (texte) => {
    setDraft(texte);
    zoneSaisieRef.current?.focus();
  };

  return (
    <div className="mx-auto flex h-full max-w-4xl flex-col px-4 py-6 sm:py-8">
      {/* En-tête : titre et description à gauche, solde IA à droite */}
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Assistant commercial</h1>
          <p className="mt-1 max-w-xl text-sm text-gray-500">
            Consulte les commandes, prépare des relances WhatsApp et crée des brouillons de commande à confirmer.
          </p>
        </div>
        {creditBalance !== null && (
          <div className="inline-flex items-center gap-2 rounded-full border border-primary-100 bg-primary-50 px-3 py-1.5 text-sm text-primary-700">
            <SparklesIcon className="h-4 w-4" aria-hidden="true" />
            <span>
              Solde IA : <span className="font-semibold tabular-nums">{creditBalance}</span> points
            </span>
          </div>
        )}
      </header>

      {/* Panneau de conversation : fil défilant en haut, confirmations et composeur ancrés en bas */}
      <div className="flex min-h-[520px] flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          {messages.length === 0 && !loading ? (
            <div className="flex h-full flex-col items-center justify-center py-10 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600" aria-hidden="true">
                <SparklesIcon className="h-6 w-6" />
              </div>
              <p className="text-base font-semibold text-gray-900">Que voulez-vous savoir sur vos commandes ?</p>
              <p className="mt-1 text-sm text-gray-500">Choisissez une suggestion ou écrivez votre demande.</p>
              <div className="mt-6 grid w-full max-w-2xl gap-2 sm:grid-cols-2">
                {EXEMPLES_DE_DEMANDES.map((exemple) => (
                  <button
                    key={exemple}
                    type="button"
                    onClick={() => utiliserSuggestion(exemple)}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-left text-sm text-gray-700 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                  >
                    {exemple}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {messages.map((item, index) => (
                <BulleMessage key={`${item.role}-${index}`} message={item} />
              ))}
              {loading && <IndicateurSaisie />}
              <div ref={finDuFilRef} />
            </div>
          )}
        </div>

        {/* Zone basse : confirmations éventuelles puis composeur */}
        <div className="space-y-3 border-t border-gray-200 bg-gray-50/60 p-3 sm:p-4">
          {pendingConfirmation?.type === 'campaign' && (
            <PanneauConfirmation
              titre="Confirmation requise"
              sousTitre={`${pendingConfirmation.recipientCount} relance(s) seront placées dans la file WhatsApp existante.`}
              libelleBouton="Confirmer l’envoi"
              chargement={sendingCampaign}
              onConfirmer={confirmCampaign}
            >
              <p className="whitespace-pre-wrap text-gray-700">{pendingConfirmation.message}</p>
            </PanneauConfirmation>
          )}

          {/* Le récapitulatif reste séparé du message pour rendre la confirmation volontaire et lisible. */}
          {pendingConfirmation?.type === 'order' && (
            <PanneauConfirmation
              titre="Confirmer la commande"
              sousTitre="Le stock et les prix seront revérifiés au moment de la création."
              libelleBouton="Créer la commande"
              chargement={sendingCampaign}
              onConfirmer={confirmOrder}
            >
              <RecapitulatifCommande commande={pendingConfirmation} />
            </PanneauConfirmation>
          )}

          {/* Composeur : textarea sans bordure dans un cadre commun, bouton d'envoi rond */}
          <div className="flex items-end gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-sm focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <Input.TextArea
              ref={zoneSaisieRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ex. Jean, 0700000000, Cocody, SKU ABC-123, livraison 1 500"
              autoSize={{ minRows: 1, maxRows: 5 }}
              disabled={loading}
              variant="borderless"
              className="flex-1 !resize-none !bg-transparent !shadow-none"
            />
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={<SendOutlined />}
              loading={loading}
              disabled={!draft.trim()}
              onClick={sendQuestion}
              aria-label="Envoyer"
            />
          </div>
          <p className="px-1 text-[11px] text-gray-400">
            Entrée pour envoyer · Maj + Entrée pour une nouvelle ligne
          </p>
        </div>
      </div>
    </div>
  );
};

export default CommerceAssistant;
